import React from 'react'
import { ReportElement as GqlReportElement } from 'gqlTypes'
import useReportElementChart from 'hooks/useReportElementChart'
import { ChartColors } from '.'
import HighchartsReact from 'highcharts-react-official'
import Highcharts from 'highcharts'
import { Box, Typography } from '@mui/material'
import ReportElementSettings from './ReportElementSettings'
import _ from 'lodash'
import useElementCohortsAndSubgroups from 'hooks/useElementCohortsAndSubgroups'
import { getCohortToUse, getSubgroupToUse } from 'util/getBarChartData'
import { ReportElementProps } from './ReportElement'

Highcharts.setOptions({
  chart: {
    style: {
      fontFamily: '"Droid Sans", sans-serif',
    },
  },
})

type datumProps = {
  x: number
  y: number
  type: string
  name: string
  color: undefined | string
}

const ReportElementScatterChart: React.FC<ReportElementProps> = ({
  reportId,
  element,
  sectionId,
  report,
  favorites = false,
  print = false,
  onUpdateFavorites,
}) => {
  const {
    subgroup,
    pastResults,
    dataToggles,
    data,
    pastResultNames,
    hidden,
    handleSetHidden,
    view,
    handleSetView,
    elementTitle,
    parsedData,
  } = useReportElementChart(element, reportId)

  try {
    const [elementCohorts, elementSubgroups] = useElementCohortsAndSubgroups(
      element,
      parsedData
    )

    const subgroupToUse: string = getSubgroupToUse(
      subgroup || '',
      elementSubgroups
    )

    let seriesData: any[] = []
    if (data && dataToggles === 'Overall') {
      seriesData = seriesData.concat(data.current || [])
      if (pastResults) {
        seriesData = seriesData.concat(data['past_results'] || [])
      }
    }
    if (data && dataToggles === 'Subgroup') {
      const subgroupData = subgroupToUse
        ? data.segmentations.filter(
            (d: { name: string }) => d.name === subgroupToUse
          )[0]
        : data.segmentations[0]
      seriesData = subgroupData?.series || []
    }

    seriesData = seriesData.map((d) => {
      d.type = 'scatter'
      return {
        ...d,
        marker: {
          symbol: 'circle',
        },
        data:
          d.type === 'scatter' && d.data
            ? d.data.map((n: { X: number; Y: number }) => {
                return {
                  x: n.X,
                  y: n.Y,
                  type: 'scatter',
                  name: d.name,
                  color: undefined,
                }
              })
            : [],
      }
    })

    const colorLength = ChartColors.length
    seriesData.forEach((d, i) => {
      d.color = ChartColors[i <= colorLength ? i : (i % colorLength) - 1]
      d.data.forEach((n: datumProps) => {
        n.color = ChartColors[i <= colorLength ? i : (i % colorLength) - 1]
      })
      d.data.sort((a: datumProps, b: datumProps) => a.x - b.x || a.y - b.y)
    })

    if (view === 'bubble') {
      seriesData = seriesData.map((d) => {
        const groupedData = _.groupBy(d.data, (n) => `${n.x} ${n.y}`)
        return {
          ...d,
          type: 'bubble',
          data: Object.keys(groupedData).map((key) => {
            const keyData = groupedData[key] || []
            return { ...keyData[0], z: keyData.length }
          }),
        }
      })
    }

    seriesData = seriesData.concat([
      {
        type: 'line',
        name: 'Y Median',
        dashStyle: 'dash',
        color: '#72818D',
        data: [
          [0, data.yMedian],
          [100, data.yMedian],
        ],
      },
      {
        type: 'line',
        name: 'X Median',
        dashStyle: 'dash',
        color: '#72818D',
        data: [
          [data.xMedian, 0],
          [data.xMedian, 100],
        ],
      },
    ])

    const chart = {
      title: undefined,
      exporting: {
        buttons: {
          contextButton: {
            enabled: false,
          },
        },
      },
      chart: {
        type: view,
        zoomType: 'xy',
        width: 700,
        height: 800,
      },
      xAxis: {
        title: {
          text: data.xLabel,
        },
        min: view === 'scatter' ? data.xMin : undefined,
        max: view === 'scatter' ? data.xMax : undefined,
        interval: data.xInterval,
        labels: {
          formatter: function (
            this: Highcharts.AxisLabelsFormatterContextObject
          ): string {
            return (this.value as number) >= 0 && (this.value as number) <= 100
              ? `${this.value}%`
              : ''
          },
        },
      },
      yAxis: {
        title: {
          text: data.yLabel,
        },
        min: view === 'scatter' ? data.yMin : undefined,
        max: view === 'scatter' ? data.yMax : undefined,
        interval: data.yInterval,
        labels: {
          formatter: function (
            this: Highcharts.AxisLabelsFormatterContextObject
          ): string {
            return (this.value as number) >= 0 && (this.value as number) <= 100
              ? `${this.value}%`
              : ''
          },
        },
      },
      credits: {
        enabled: false,
      },
      series: seriesData.filter((d) => !hidden.includes(d.name)),
    }

    const showHideOptions = seriesData
      .filter((item: { name: string }) =>
        pastResults ? true : !pastResultNames.includes(item.name)
      )
      .map((item: { name: string }) => ({
        key: item.name,
        title: item.name,
      }))

    return (
      <Box sx={{ mb: 4, width: '100%', breakInside: 'avoid' }}>
        {elementTitle && (
          <Box px={4}>
            <Typography variant="h5">{elementTitle}</Typography>
          </Box>
        )}
        <Box mt={2}>
          <ReportElementSettings
            print={print}
            reportId={reportId}
            element={element}
            sectionId={sectionId}
            report={report}
            noSubgroup={
              dataToggles === 'Overall' || data.segmentations.length === 0
            }
            noCohort={true}
            noPastResults={
              dataToggles != 'Overall' || data['past_results'].length === 0
            }
            view={view}
            viewOptions={[
              { title: 'Bubble', value: 'bubble' },
              { title: 'Scatter', value: 'scatter' },
            ]}
            showHideOptions={showHideOptions}
            hidden={hidden}
            onChangeHidden={handleSetHidden}
            onChangeView={handleSetView}
            favorites={favorites}
            onUpdateFavorites={onUpdateFavorites}
          />
        </Box>
        <Box p={4}>
          <HighchartsReact highcharts={Highcharts} options={chart} />
        </Box>
      </Box>
    )
  } catch (error) {
    console.error(error)
    return null
  }
}

export default ReportElementScatterChart
